import React, { useState } from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useQuery } from '@apollo/client';
import { GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS } from '../../../graph-queries/tar/certificate';
import { TCertificate } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import { ColumnDef } from '@tanstack/react-table';
import { useAlertText } from '../../organisms/AspectsTable/util';
import { useNavigate } from 'react-router-dom';
import { geCertificateExecutionPath } from '../../../constants';
import CertificateFiltersModal from '../../organisms/CertificateFilter/CertificateFiltersModal';
import { MONITOR_CERTIFICATE_FILTERS } from '../../../lib/constants';
import { useBrowserLocationState } from '../../../lib/util/browser';
import { certificatesGlobalFilterFn } from '../../organisms/CertificateFilter/CertificateFilter.util';
import { getStepStatusIcon, ActionColumn } from './CertificateMonitoring.util';
import { CertificateStatusEnum } from '../../../graph-queries/__generated__/graphql';
import FormModal from '../../molecules/FormModal/FormModal';
import MC1Attachment from './MC1Attachment';
import { AddComment16, CircleCheck24, Question24 } from '@bphxd/ds-core-react/lib/icons';
import classes from './CertificateMonitoring.module.scss';
import SH1Attachment from './SH1Attachment';
import { CertificateMonitoringActions } from './CertificateMonitoringActions';
import { CertificateMonitoringDashboard } from './CertificateMonitoringDashboard';

export const stepTitles = [
  'Joint Broken',
  'Joint Assembled',
  'Joint Tightened',
  'Joint Witnessed',
  'Leak/Service Test',
  'Start Up',
  '12 HR',
  '24 HR',
  '36 HR',
  '48 HR',
];

export const stepColumns: ColumnDef<TCertificate>[] = stepTitles.map((title, index) => {
  const stepNo = index + 1;
  return {
    header: title,
    enableSorting: false,
    cell: (props) => getStepStatusIcon(props.row.original, stepNo, title),
  };
});

export const columns: ColumnDef<TCertificate>[] = [
  {
    header: 'Certificate Title',
    accessorKey: 'title',
    cell: (props) => {
      const asset = useSelectedAsset();
      const navigate = useNavigate();
      const row = props.row.original;
      const certComments = row.comments || '';
      return (
        <div className={classes.certificateTitleContainer}>
          <a
            href={geCertificateExecutionPath({
              asset: asset?.assetName || '',
              certificateId: row.certificateId,
            })}
            onClick={(e) => {
              e.preventDefault();
              navigate(
                geCertificateExecutionPath({
                  asset: asset?.assetName || '',
                  certificateId: row.certificateId,
                })
              );
            }}
            style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
          >
            {row.title}
          </a>

          {certComments && (
            <div className={classes.certificateCommentsContainer}>
              <span className={classes.certificateCommentsSpan}>
                <AddComment16 />
              </span>
            </div>
          )}
        </div>
      );
    },
  },
  {
    header: 'Certificate Number',
    accessorKey: 'certificateNumber',
    accessorFn: (row) => row.certificateNumber || row.certificateId,
  },
  {
    header: 'System',
    accessorKey: 'system',
  },
  {
    header: 'MC1',
    accessorKey: 'maintenanceMC1.mc1Number',
    enableSorting: false,
    cell: (props) => {
      const item = props.row.original.maintenanceMC1;
      if (!item) return 'N/A';

      return (
        <FormModal
          title="MC1 Attachment"
          renderForm={(onClose) => <MC1Attachment mc1={item} onClose={onClose} />}
          renderButton={(onClick) =>
            item.mediaUrl ? (
              <CircleCheck24 className={classes.success} onClick={onClick} title={item.mc1Number} />
            ) : (
              <Question24 className={classes.error} onClick={onClick} title={item.mc1Number} />
            )
          }
        />
      );
    },
  },
  {
    header: 'SH1',
    accessorKey: 'maintenanceSH1.sh1Number',
    enableSorting: false,
    cell: (props) => {
      const item = props.row.original.maintenanceSH1;
      if (!item) return 'N/A';

      return (
        <FormModal
          title="SH1 Attachment"
          renderForm={(onClose) => <SH1Attachment sh1={item} onClose={onClose} />}
          renderButton={(onClick) =>
            item.mediaUrl ? (
              <CircleCheck24 className={classes.success} onClick={onClick} title={item.sh1Number} />
            ) : (
              <Question24 className={classes.error} onClick={onClick} title={item.sh1Number} />
            )
          }
        />
      );
    },
  },
  ...stepColumns,
  {
    header: 'Action',
    enableSorting: false,
    cell: (props) => (
      <CertificateMonitoringActions certificateId={props.row.original.certificateId} />
    ),
  },
];

const CertificateMonitoringPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();

  const asset = useSelectedAsset();
  const [filters, setFilters] = useBrowserLocationState('filters', MONITOR_CERTIFICATE_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { data, loading } = useQuery(GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const approvedCertificates =
    (data?.allCertificates.filter(
      (cert) => cert.status === CertificateStatusEnum.Approved && cert.template === false
    ) as unknown as TCertificate[]) || [];

  const isLoading = loading || !!alertText;

  return (
    <Layout>
      <Container>
        {!isLoading && (
          <>
            <CertificateMonitoringDashboard
              certificates={approvedCertificates}
              onSubmit={setFilters}
            />

            {isFilterOpen && (
              <CertificateFiltersModal
                defaultValues={filters}
                onSubmit={setFilters}
                onReset={() => {
                  setFilters(MONITOR_CERTIFICATE_FILTERS);
                  setIsFilterOpen(false);
                }}
                onClose={() => setIsFilterOpen(false)}
                showMonitoringFilters={true}
              />
            )}
          </>
        )}
        {isLoading ? (
          'Loading...'
        ) : (
          <AspectsTable<TCertificate>
            hideSelectionColumn
            title="In Progress Certificates"
            aspectName="certificate"
            data={approvedCertificates}
            columns={columns}
            alertText={alertText}
            creationDisabled={true}
            getRowMutationDisabled={(_row) => false}
            getRowDeletionDisabled={(_row) => false}
            searchEnabled={true}
            searchPlaceholderText="Search for certificates..."
            filters={filters}
            onFilterClick={() => setIsFilterOpen(true)}
            onUpdateFilters={(newFilters) => setFilters(newFilters)}
            globalFilterFn={certificatesGlobalFilterFn}
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default CertificateMonitoringPage;
